import React from "react";
import { withFirebase } from "../Firebase";
import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import withAuthorization from "../Session/withAuthorization";
import Navigation from "../Navigation";
import { DropzoneAreaBase } from "material-ui-dropzone";
import {
  Container,
  Button,
  Divider,
  Snackbar,
  CircularProgress,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import "./css/requestUpload.css";
import Functions from "./functions";
import * as ROUTES from "../../constants/routes";
import * as XLSX from "xlsx";
import { useState } from "react";
import { useEffect } from "react";

const resolveUploadType = ({ location, uploadType, name }) => {
  if (uploadType) {
    return uploadType;
  }

  if (location.pathname === ROUTES.INVESTMENT_REQUEST_UPLOAD) {
    return "booking";
  }

  if (location.pathname === ROUTES.WITHDRAWAL_REQUEST_UPLOAD) {
    return "redemption";
  }

  if (name === "Investment Transactions") {
    return "booking";
  }

  return "redemption";
};

const RequestUpload = () => {
  return <Navigation content={<RequestUploadPage />} />;
};

const RequestUploadBase = (props) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");

  const [fundRate, setFundRate] = useState(null);
  const [prevFundRate, setPrevFundRate] = useState(null);

  const [varianceOpen, setVarianceOpen] = useState(false);
  const [varianceMessage, setVarianceMessage] = useState("");

  const [uploadResults, setUploadResults] = useState(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);

  const uploadType = resolveUploadType(props);
  const isInvestment = uploadType === "booking";
  const email = props.firebase.auth.currentUser.email;

  const functions = new Functions();

  async function getDetails() {
    const data = await props.firebase.db
      .collection("fundRates")
      .orderBy("dateUploaded", "desc")
      .limit(2)
      .get();
    setFundRate(data.docs[0].data());
    setPrevFundRate(data.docs[1].data());
  }

  useEffect(() => {
    getDetails();
  }, []);

  const handleDropzone = (file) => {
    setUploadedFile(file[0]);
  };

  const submit = () => {
    setLoading(true);
    try {
      if (!uploadedFile) {
        throw new Error("No File Detected");
      }
      validateFile();
    } catch (err) {
      setLoading(false);
      setAlertOpen(true);
      setSeverity("error");
      setMessage(err.message);
    }
  };

  const validateFile = () => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];

        const sheet = workbook.Sheets[sheetName];

        const sheetData = XLSX.utils.sheet_to_json(sheet);
        const fundData = await props.firebase.db.collection("funds").get();
        const fundDocs = fundData.docs.map((doc) => doc.data());

        let index = 1;
        for (const obj of sheetData) {
          const fund = fundRate.fundDetails.find(
            (x) => x.fundName === obj["Fund Name"]
          );
          const fundCode = fundDocs.find((x) => x.code === obj["Fund Code"]);
          const prevFund = prevFundRate.fundDetails.find(
            (x) => x.fundName === obj["Fund Name"]
          );

          const navps = obj["NAVPS Booking Time"]
            ? obj["NAVPS Booking Time"]
            : 0;

          if (!fundCode) {
            throw new Error(`
              For Row: ${index}\n
          Transaction ID: ${obj["Transaction ID"]}\n 
             Invalid Fund Code
              `);
          }

          if (isInvestment) {
            const sharesSys = obj["Total Amount"] / parseFloat(fund.rate);
            const sharesExcel =
              obj["Total Amount"] / parseFloat(obj["NAVPS Booking Time"]);

            const roundingVariance = 0.05;
            const variance = Math.abs(sharesSys - sharesExcel);
            if (variance > roundingVariance) {
              throw new Error(`
              For Row: ${index}\n
              Transaction ID: ${obj["Transaction ID"]}\n 
              Entered shares and expected shares do not match up with a difference of: ${variance}
              `);
            }
          } else {
            // if (obj['Status'] === 'declined') {
            //   return alert('Declined status is not available for redemptions');
            // }

            const amtInvestedSys = parseFloat(fund.rate) * obj.Shares;
            const amtInvestedExcel = parseFloat(navps) * obj.Shares;

            const roundingVariance = 0.05;
            const variance = Math.abs(amtInvestedSys - amtInvestedExcel);

            if (variance > roundingVariance) {
              throw new Error(`
              For Row: ${index}\n
              Transaction ID: ${obj["Transaction ID"]}\n 
              Entered shares and expected shares do not match up with a difference of: ${variance}
              `);
            }
          }

          const navpsVariance = parseFloat(prevFund.rate) * 0.05;
          const actualVariance = Math.abs(navps - parseFloat(prevFund.rate));

          if (actualVariance > navpsVariance) {
            throw new Error(`
            For Row: ${index}\n
            Transaction ID: ${obj["Transaction ID"]}\n
            Exceeds previous variance of NAVPS: ${actualVariance.toFixed(2)}`);
          }
          index += 1;
        }

        uploadFile();
      } catch (err) {
        setVarianceOpen(true);
        setVarianceMessage(err.message);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(uploadedFile.file);
  };

  async function uploadFile() {
    setVarianceOpen(false);
    setLoading(true);
    try {
      const res = await functions.doUploadFile(
        uploadedFile,
        email,
        uploadType
      );

      if (res.data.ok) {
        setUploadedFile(null);
        setLoading(false);

        if (res.data.data) {
          setUploadResults(res.data.data);
          setResultsDialogOpen(true);
        }

        setAlertOpen(true);
        setSeverity("success");
        setMessage(res.data.message || "Successfully uploaded file");
      } else {
        throw new Error(res.data.message);
      }
    } catch (err) {
      setLoading(false);
      setAlertOpen(true);
      setSeverity("error");
      setMessage(err.message);
    }
  }

  return (
    <div className={`${!props.name && "cms-main-content"}`}>
      <div
        className={`${!props.name && "body-content"}`}
        style={{ margin: "auto" }}
      >
        <Container>
          {!props.name && (
            <>
              <h1>Upload {isInvestment ? "Booking" : "Redemption"}</h1>
              <span>
                This page is specifically for uploading approved{" "}
                {isInvestment ? " or booked" : " redeemed"} investments
              </span>
              <br /> <br />
              <div>
                <span>
                  Note: The excel file must contain ONLY{" "}
                  {isInvestment ? `'approved'` : `'redeemed'`} entries in the
                  status column.
                </span>
                <br />
                <span>Instructions:</span>
                <ol>
                  <li>Click the upload box or drag file</li>
                  <li>
                    Select {isInvestment ? "booking" : "redemption"} file. NOTE:
                    Extension should be .xlsx
                  </li>
                  <li>Click submit</li>
                </ol>
                <span>
                  The <b>Date Processed</b> column must follow the formatting{" "}
                  <b>(YYYY-MM-DD)</b>.
                </span>
                <br />
              </div>
            </>
          )}
          <br />

          <DropzoneAreaBase
            acceptedFiles={[
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "xls",
            ]}
            filesLimit={1}
            showPreviewsInDropzone={true}
            onAdd={handleDropzone}
          />
          {uploadedFile && (
            <label style={{ fontSize: "14px", fontWeight: "600" }}>
              {uploadedFile.file.name}
            </label>
          )}
          <Divider
            style={{
              marginTop: `${props.name ? "10px" : "50px"}`,
              marginBottom: "30px",
            }}
          />
          <div
            className={`${props.name ? "flex justify-center" : "flex"}`}
            style={{ alignItems: "center", gap: "16px" }}
          >
            {loading ? (
              <CircularProgress />
            ) : (
              <Button
                variant="contained"
                className={"approve-button"}
                disabled={uploadedFile === null}
                onClick={submit}
              >
                Submit
              </Button>
            )}
          </div>
        </Container>
        <Snackbar
          open={alertOpen}
          autoHideDuration={6000}
          onClose={() => setAlertOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert severity={severity}>{message}</Alert>
        </Snackbar>
        <Dialog
          fullWidth
          maxWidth={"sm"}
          open={varianceOpen}
          onClose={() => setVarianceOpen(false)}
        >
          <DialogTitle>{"Inconsistency Detected"}</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: "15px" }}>
              {varianceMessage.split("\n").map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
            {varianceMessage.includes("Invalid Fund Code") ? (
              ""
            ) : (
              <b>Proceeding will upload the file</b>
            )}
          </DialogContent>
          {varianceMessage.includes("Invalid Fund Code") ? (
            ""
          ) : (
            <DialogActions>
              <Button onClick={() => setVarianceOpen(false)} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={uploadFile}
                color="primary"
                autoFocus
              >
                Proceed Anyway
              </Button>
            </DialogActions>
          )}
        </Dialog>
        <UploadResultsDialog
          open={resultsDialogOpen}
          onClose={() => setResultsDialogOpen(false)}
          results={uploadResults}
          isInvestment={isInvestment}
        />
      </div>
    </div>
  );
};

const STATUS_CHIP_COLORS = {
  success: { backgroundColor: "#4caf50", color: "#fff" },
  reprocessed: { backgroundColor: "#2196f3", color: "#fff" },
  failed: { backgroundColor: "#f44336", color: "#fff" },
  declined: { backgroundColor: "#ff9800", color: "#fff" },
};

const UploadResultsDialog = ({ open, onClose, results, isInvestment }) => {
  if (!results) return null;

  const { total = 0, succeeded = 0, failed = 0, reprocessed = 0 } = results;
  const rows = results.results || [];

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>
        Upload Results — {isInvestment ? "Booking" : "Redemption"}
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          justifyContent="space-around"
          mb={2}
          p={1.5}
          borderRadius={4}
          style={{ backgroundColor: "#f5f5f5" }}
        >
          <ResultStat label="Total" value={total} />
          <ResultStat label="Succeeded" value={succeeded} color="#4caf50" />
          <ResultStat label="Failed" value={failed} color="#f44336" />
          <ResultStat label="Reprocessed" value={reprocessed} color="#2196f3" />
        </Box>
        {rows.length > 0 && (
          <TableContainer
            component={Paper}
            variant="outlined"
            style={{ maxHeight: 400 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Event ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell
                      style={{ fontFamily: "monospace", fontSize: 13 }}
                    >
                      {row.eventId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        style={STATUS_CHIP_COLORS[row.status] || {}}
                      />
                    </TableCell>
                    <TableCell style={{ fontSize: 13 }}>
                      <ResultDetails row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ResultStat = ({ label, value, color }) => (
  <Box textAlign="center">
    <Typography
      variant="h5"
      style={{ fontWeight: 700, color: color || "inherit" }}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
  </Box>
);

const ResultDetails = ({ row }) => {
  if (row.status === "failed" && row.error) {
    return <span style={{ color: "#f44336" }}>{row.error}</span>;
  }
  if (row.status === "reprocessed") {
    return (
      <span>
        Shares: {row.previousShares} → {row.newShares}
      </span>
    );
  }
  return <span>—</span>;
};

const condition = (authUser) => !!authUser;

const RequestUploadPage = compose(
  withAuthorization(condition),
  withRouter,
  withFirebase
)(RequestUploadBase);

export { RequestUploadPage };

export default RequestUpload;
